import React from 'react';

const SWIPE_MIN_DISTANCE = 50;
const SWIPE_AXIS_RATIO = 1.5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_RADIUS = 30;
const TAP_TOLERANCE = 10;
const WHEEL_FACTOR = 1.15;
const BUTTON_FACTOR = 1.5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getDistance = (touches) =>
  Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);

const getMidpoint = (touches) => ({
  x: (touches[0].clientX + touches[1].clientX) / 2,
  y: (touches[0].clientY + touches[1].clientY) / 2
});

// Single gesture controller for the main image stage: wheel/pinch/double-tap zoom,
// drag-to-pan while zoomed, and horizontal swipe navigation while NOT zoomed.
// Consolidated in one hook because pan and swipe share the same touch events.
export default ({ maxScale = 4, doubleTapScale = 2.5, onSwipeLeft, onSwipeRight }) => {
  const [transform, setTransform] = React.useState({ scale: 1, x: 0, y: 0 });
  const [gesturing, setGesturing] = React.useState(false);
  const containerRef = React.useRef(null);
  const gestureRef = React.useRef(null);
  const lastTapRef = React.useRef(null);
  const lastTouchAtRef = React.useRef(0);

  const clampTransform = React.useCallback(
    (next) => {
      const rect = containerRef.current?.getBoundingClientRect();
      const scale = clamp(next.scale, 1, maxScale);
      if (!rect || scale === 1) return { scale, x: 0, y: 0 };
      const maxX = ((scale - 1) * rect.width) / 2;
      const maxY = ((scale - 1) * rect.height) / 2;
      return { scale, x: clamp(next.x, -maxX, maxX), y: clamp(next.y, -maxY, maxY) };
    },
    [maxScale]
  );

  const reset = React.useCallback(() => setTransform({ scale: 1, x: 0, y: 0 }), []);

  // Zoom keeping the given client point visually fixed (null point = stage center)
  const zoomAt = React.useCallback(
    (point, getScale) => {
      setTransform((prev) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return prev;
        const scale = clamp(getScale(prev.scale), 1, maxScale);
        const cx = point ? point.x - rect.left - rect.width / 2 : 0;
        const cy = point ? point.y - rect.top - rect.height / 2 : 0;
        const ratio = scale / prev.scale;
        return clampTransform({
          scale,
          x: cx - (cx - prev.x) * ratio,
          y: cy - (cy - prev.y) * ratio
        });
      });
    },
    [clampTransform, maxScale]
  );

  // Wheel zoom needs preventDefault (trackpad pinch = ctrl+wheel zooms the page),
  // so attach natively as non-passive — React's delegated wheel listener is passive
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleWheel = (event) => {
      event.preventDefault();
      const factor = event.deltaY < 0 ? WHEEL_FACTOR : 1 / WHEEL_FACTOR;
      zoomAt({ x: event.clientX, y: event.clientY }, (scale) => scale * factor);
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [zoomAt]);

  const toggleZoom = (point) => {
    if (transform.scale > 1) {
      reset();
    } else {
      zoomAt(point, () => doubleTapScale);
    }
  };

  const onDoubleClick = (event) => {
    // Skip if synthesized from a touch double-tap (already handled in onTouchEnd)
    if (Date.now() - lastTouchAtRef.current < 500) return;
    toggleZoom({ x: event.clientX, y: event.clientY });
  };

  const onMouseDown = (event) => {
    if (transform.scale === 1 || event.button !== 0) return;
    event.preventDefault();
    const start = { x: event.clientX, y: event.clientY, tx: transform.x, ty: transform.y };
    setGesturing(true);
    const onMove = (moveEvent) =>
      setTransform((prev) =>
        clampTransform({
          ...prev,
          x: start.tx + moveEvent.clientX - start.x,
          y: start.ty + moveEvent.clientY - start.y
        })
      );
    const onUp = () => {
      setGesturing(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onTouchStart = (event) => {
    if (event.touches.length === 2) {
      gestureRef.current = {
        mode: 'pinch',
        startDistance: getDistance(event.touches),
        startScale: transform.scale
      };
      setGesturing(true);
    } else if (event.touches.length === 1) {
      const touch = event.touches[0];
      gestureRef.current = {
        mode: 'touch',
        startX: touch.clientX,
        startY: touch.clientY,
        startTx: transform.x,
        startTy: transform.y
      };
      if (transform.scale > 1) setGesturing(true);
    }
  };

  const onTouchMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    if (gesture.mode === 'pinch' && event.touches.length === 2) {
      const factor = getDistance(event.touches) / gesture.startDistance;
      zoomAt(getMidpoint(event.touches), () => gesture.startScale * factor);
    } else if (gesture.mode === 'touch' && event.touches.length === 1 && transform.scale > 1) {
      const touch = event.touches[0];
      setTransform((prev) =>
        clampTransform({
          ...prev,
          x: gesture.startTx + (touch.clientX - gesture.startX),
          y: gesture.startTy + (touch.clientY - gesture.startY)
        })
      );
    }
  };

  const onTouchEnd = (event) => {
    lastTouchAtRef.current = Date.now();
    const gesture = gestureRef.current;
    setGesturing(false);
    if (!gesture || event.touches.length > 0) return;
    gestureRef.current = null;
    if (gesture.mode !== 'touch') return;

    const touch = event.changedTouches[0];
    const dx = touch.clientX - gesture.startX;
    const dy = touch.clientY - gesture.startY;

    // Double-tap to zoom
    if (Math.abs(dx) < TAP_TOLERANCE && Math.abs(dy) < TAP_TOLERANCE) {
      const now = Date.now();
      const lastTap = lastTapRef.current;
      const isDoubleTap =
        lastTap &&
        now - lastTap.time < DOUBLE_TAP_MS &&
        Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < DOUBLE_TAP_RADIUS;
      lastTapRef.current = isDoubleTap ? null : { time: now, x: touch.clientX, y: touch.clientY };
      if (isDoubleTap) toggleZoom({ x: touch.clientX, y: touch.clientY });
      return;
    }

    // Swipe navigation only while unzoomed (zoomed drags are pans)
    if (
      transform.scale === 1 &&
      Math.abs(dx) >= SWIPE_MIN_DISTANCE &&
      Math.abs(dx) >= Math.abs(dy) * SWIPE_AXIS_RATIO
    ) {
      if (dx < 0) {
        onSwipeLeft && onSwipeLeft();
      } else {
        onSwipeRight && onSwipeRight();
      }
    }
  };

  return {
    containerRef,
    scale: transform.scale,
    isZoomed: transform.scale > 1,
    zoomIn: () => zoomAt(null, (scale) => scale * BUTTON_FACTOR),
    zoomOut: () => zoomAt(null, (scale) => scale / BUTTON_FACTOR),
    reset,
    imageStyle: {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(${transform.scale})`,
      transition: gesturing ? 'none' : 'transform 0.25s ease',
      cursor: transform.scale > 1 ? (gesturing ? 'grabbing' : 'grab') : 'zoom-in'
    },
    handlers: { onMouseDown, onTouchStart, onTouchMove, onTouchEnd, onDoubleClick }
  };
};
